// File: frontend/src/context/AgentContext.js
//
// The agent portal's session.
//
// ── A FAILED REQUEST IS NOT AN INVALID SESSION ──────────────────────────────
//
// The mount-time hydration used to end `.catch(() => logout())`. Every failure
// was read as "you are not who you say you are": a dropped connection on a phone
// walking between masts, a 502 from the proxy during a deploy, and — worst,
// because it is deliberate — the 503 that authenticateAgent returns when its
// status check cannot reach the database. That guard fails CLOSED on purpose so
// a dropped connection cannot re-admit a deactivated agent; the cost of reading
// it as a logout was that one database blip signed out every agent in the field,
// mid-sale, and made them find their PIN again.
//
// This file now makes the same separation PermissionsContext makes, for the same
// reason and in the same shape — `error` is "auth" or "network", never a claim
// about identity — because two contexts inventing two answers to one question is
// how they come to disagree.
//
//     401 (any route)              the token is missing, invalid or expired
//     403 + code AGENT_INACTIVE    the agent row is gone or deactivated
//        → both are the server stating, from the record, that this session is
//          over. Log out.
//
//     no response (network/CORS)   we never reached the server
//     5xx, 503 "try again"         the server could not answer
//     200 with success:false       the route could not produce a dashboard
//        → none of these say anything about the session. Keep it, surface a
//          retryable error, and let the reader try again.
//
// A bare 403 without the code is deliberately in the second group. On the agent
// routes it means "not an agent token", which is unusable — but the same status
// is also how a resource-level refusal is expressed, and a context that logs
// people out on a status it cannot interpret is the defect this file just fixed.
// A session that cannot do anything will keep failing visibly; that is recoverable.
// A session destroyed by mistake is not.

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import axios from "axios";
import { handleSessionExpired } from "../utils/authNotifications";
import { API_URL as BASE_URL } from "../config/apiBase";

const AgentContext = createContext(null);
const STORAGE_KEY = "agent_token";

// The ONLY signals that end a session. Everything else is retryable.
// Exported so a test asserts the rule itself, not a re-implementation of it.
export function isSessionInvalid(err) {
  const status = err?.response?.status;
  if (status === 401) return true;
  return status === 403 && err?.response?.data?.code === "AGENT_INACTIVE";
}

/** "auth" (the session is over) or "network" (we could not tell — retryable). */
export function classifyAgentError(err) {
  return isSessionInvalid(err) ? "auth" : "network";
}

export function AgentProvider({ children }) {
  const [token,         setToken]         = useState(() => localStorage.getItem(STORAGE_KEY));
  const [agent,         setAgent]         = useState(null);
  const [billingStatus, setBillingStatus] = useState(null); // ← NEW
  const [loading,       setLoading]       = useState(() => !!localStorage.getItem(STORAGE_KEY));
  // null | "auth" | "network". "network" NEVER means the session ended.
  const [error,         setError]         = useState(null);
  const [loadedOnce,    setLoadedOnce]    = useState(false);
  const [reloadTick,    setReloadTick]    = useState(0);

  /** Retry the hydration the UI is showing an error for. */
  const reload = useCallback(() => setReloadTick((n) => n + 1), []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setToken(null);
    setAgent(null);
    setBillingStatus(null);
    setLoading(false);
    setError(null);
    setLoadedOnce(false);
  }, []);

  const agentApi = useMemo(() => {
    const instance = axios.create({ baseURL: BASE_URL });

    // Attach token on every request
    instance.interceptors.request.use((config) => {
      const t = localStorage.getItem(STORAGE_KEY);
      if (t) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${t}`;
      }
      return config;
    });

    // Auto-logout on 401; read subscription warning headers
    instance.interceptors.response.use(
      (response) => {
        // Read warning headers injected by subscriptionGuard middleware
        const warning  = response.headers?.["x-subscription-warning"];
        const trialLeft = response.headers?.["x-trial-days-left"];

        if (warning) {
          // Format: "grace:3d"
          const [statusKey, rest] = warning.split(":");
          const days = rest ? parseInt(rest, 10) : 0;
          setBillingStatus((prev) => ({
            ...(prev || {}),
            effectiveStatus:    statusKey,
            daysLeftInGrace:    days,
          }));
        }
        if (trialLeft !== undefined) {
          const days = parseInt(trialLeft, 10);
          setBillingStatus((prev) => ({
            ...(prev || {}),
            effectiveStatus:  "trial",
            daysLeftInTrial:  days,
          }));
        }

        return response;
      },
      (err) => {
        // Only a stated session invalidation. A 500, a 503 or a dead connection
        // reaches the caller untouched, so the screen that made the request
        // decides what to show — it must not take the whole portal with it.
        if (isSessionInvalid(err)) {
          handleSessionExpired({
            storageKeys: [STORAGE_KEY],
            loginPath: "/agent/login",
            message: err?.response?.data?.code === "AGENT_INACTIVE"
              ? "This account is no longer active. Contact your admin."
              : undefined,
          });
          setToken(null);
          setAgent(null);
          setBillingStatus(null);
          setLoading(false);
          setError("auth");
          setLoadedOnce(false);
        }
        return Promise.reject(err);
      }
    );

    return instance;
  }, []);

  const login = useCallback(async (phone, pin) => {
    const res = await axios.post(`${BASE_URL}/agent-auth/login`, { phone, pin });
    if (res.data?.success && res.data?.token) {
      localStorage.setItem(STORAGE_KEY, res.data.token);
      setToken(res.data.token);
      setAgent(res.data.agent || null);
      setLoading(false);
      setError(null);
      return { success: true };
    }
    return {
      success: false,
      error: res.data?.error || "Login failed",
    };
  }, []);

  // On mount (or token change), hydrate agent + billing from dashboard.
  //
  // This request exists to fill in the agent's name, float and billing banner. It
  // is NOT the authority on whether the session is valid — the server says that,
  // with a 401 or an AGENT_INACTIVE 403, and only then do we act on it.
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    agentApi
      .get("/agents/portal/dashboard")
      .then((r) => {
        if (cancelled) return;
        if (r.data?.success) {
          setAgent(r.data.agent || null);
          // billing_status is returned by the updated dashboard route
          if (r.data.billing_status) {
            setBillingStatus(r.data.billing_status);
          }
          setError(null);
          setLoadedOnce(true);
        } else {
          // A 200 that says success:false is a route that could not build a
          // dashboard. It is not the server disowning the token.
          console.warn("[agent] portal/dashboard returned success:false — load error, not a logout");
          setError("network");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (classifyAgentError(err) === "auth") {
          // The interceptor above has already cleared storage and started the
          // redirect for these. Calling logout keeps the in-memory state honest
          // for the frame before the navigation lands.
          logout();
          setError("auth");
          return;
        }
        console.warn(
          `[agent] hydrate failed (network) status=${err?.response?.status || "none"} — session kept`
        );
        setError("network");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, agentApi, logout, reloadTick]);

  return (
    <AgentContext.Provider
      value={{
        agent,
        setAgent,
        token,
        loading,
        login,
        logout,
        agentApi,
        billingStatus,        // ← exposed
        setBillingStatus,     // ← exposed so pages can update it if needed
        // null | "auth" | "network". A screen may show "network" and offer
        // `reload`; it must never read it as "signed out".
        error,
        loadedOnce,
        reload,
      }}
    >
      {children}
    </AgentContext.Provider>
  );
}

export function useAgent() {
  const ctx = useContext(AgentContext);
  if (!ctx) throw new Error("useAgent must be used within AgentProvider");
  return ctx;
}
